import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Check, HandHelping } from 'lucide-react';
import type { AssistRequestDto } from '@gc/shared';

/**
 * The "someone raised a hand" banner over the device board (wave 3 #13).
 * Oldest request first; each row carries Ack (on the way) and Resolve
 * (handled). The mute toggle silences the BEEP only — the banner itself never
 * hides while a request is open, because a silenced alarm is still an alarm.
 */
export function AssistBanner({
  requests,
  now,
  muted,
  onToggleMuted,
  onAck,
  onResolve,
  actionError,
}: {
  requests: readonly AssistRequestDto[];
  now: number;
  muted: boolean;
  onToggleMuted: () => void;
  onAck: (id: string) => void;
  onResolve: (id: string) => void;
  actionError: string | null;
}) {
  const { t } = useTranslation();
  if (requests.length === 0) return null;

  return (
    <div
      data-assist-banner
      role="status"
      className="mx-4 mb-2 rounded-card border border-ending/40 bg-ending/10 px-3 py-2"
    >
      <div className="flex items-center gap-2">
        <HandHelping className="h-4 w-4 shrink-0 text-ending" aria-hidden />
        <span className="flex-1 text-sm font-medium text-content">
          {t('deviceBoard.assist.bannerTitle', { count: requests.length })}
        </span>
        <button
          type="button"
          onClick={onToggleMuted}
          title={muted ? t('deviceBoard.assist.unmute') : t('deviceBoard.assist.mute')}
          aria-label={muted ? t('deviceBoard.assist.unmute') : t('deviceBoard.assist.mute')}
          aria-pressed={muted}
          className="grid h-7 w-7 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-content"
        >
          {muted ? (
            <BellOff className="h-4 w-4" aria-hidden />
          ) : (
            <Bell className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>

      <ul className="mt-1 space-y-1">
        {requests.map((request) => (
          <li
            key={request.id}
            data-assist-request={request.status}
            className="flex flex-wrap items-center gap-2 rounded-control bg-raised/60 px-2 py-1.5"
          >
            <span className="text-sm font-medium text-content">{request.deviceName}</span>
            <span className="tabular text-xs text-content-muted">
              {t('deviceBoard.assist.minutesAgo', {
                count: Math.max(
                  0,
                  Math.floor((now - new Date(request.createdAt).getTime()) / 60_000),
                ),
              })}
            </span>
            {request.status === 'ACKED' ? (
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
                {t('deviceBoard.assist.onTheWay')}
              </span>
            ) : null}
            <span className="flex-1" />
            {request.status === 'OPEN' ? (
              <button
                type="button"
                onClick={() => onAck(request.id)}
                className="rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary hover:bg-hover"
              >
                {t('deviceBoard.assist.ack')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onResolve(request.id)}
              className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-fg hover:bg-primary-hover"
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              {t('deviceBoard.assist.resolve')}
            </button>
          </li>
        ))}
      </ul>

      {actionError ? (
        <p role="alert" className="mt-1 text-xs text-danger">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}
